import { z } from "zod";
import type { QuerySecurity } from "@/api/queries/types";
import type { Portfolio } from "@/api/types";
import type { BuyOrderRow } from "@/forms/forms/orders/orderForm/components/BuyOrders";
import type { SellOrderRow } from "@/forms/forms/orders/orderForm/components/SellOrders";
import type { DocProducerDocument } from "@/views/docproducer";

const legacySchema = z.object({
  standardParams: z.record(z.string(), z.unknown()),
  documents: z.array(
    z.object({
      id: z.string().min(1),
      type: z.string().min(1),
      data: z.record(z.string(), z.unknown()),
    })
  ),
});

function text(value: unknown): string {
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
}

function number(value: unknown): number | undefined {
  if (typeof value === "number")
    return Number.isFinite(value) ? value : undefined;
  if (typeof value !== "string" || !value.trim()) return undefined;
  const cleaned = value.replace(/NOK|%|\s/g, "").replace(",", ".");
  const parsed = Number(cleaned);
  return cleaned !== "" && Number.isFinite(parsed) ? parsed : undefined;
}

function object(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? Object.fromEntries(Object.entries(value))
    : {};
}

export function getImportedProfile(parsed: unknown): Record<string, string> {
  const root = object(parsed);
  const profile: Record<string, string> = {};
  const setProfileValue = (key: unknown, value: unknown) => {
    if (
      typeof key === "string" &&
      key &&
      (typeof value === "string" ||
        typeof value === "boolean" ||
        (typeof value === "number" && Number.isFinite(value)))
    ) {
      profile[key] = String(value);
    }
  };
  if (Array.isArray(root.kundeprofil)) {
    for (const item of root.kundeprofil) {
      const row = object(item);
      const valueKey =
        row.primaryType === "java.lang.Boolean"
          ? "booleanValue"
          : row.primaryType === "java.lang.Double"
          ? "doubleValue"
          : row.primaryType === "java.lang.Integer" ||
            row.primaryType === "java.lang.Long"
          ? "intValue"
          : row.primaryType === "java.util.Date"
          ? "dateValue"
          : "stringValue";
      setProfileValue(row.attributeKey, row[valueKey]);
    }
  }
  if (Array.isArray(root.documents)) {
    for (const document of root.documents) {
      const data = object(object(document).data);
      const afterSign = object(object(data.docProdOut).afterSign);
      if (Array.isArray(afterSign.importProfile)) {
        for (const entry of afterSign.importProfile) {
          const row = object(entry);
          setProfileValue(row.profKey, row.profVal);
        }
      }
      if (Array.isArray(afterSign.updateMainContact)) {
        for (const entry of afterSign.updateMainContact) {
          for (const [key, value] of Object.entries(object(entry))) {
            setProfileValue(key, value);
          }
        }
      }
    }
  }
  return profile;
}

export function restoreLegacyDocuments(
  parsed: unknown,
  contactId: string,
  portfolios: Portfolio[],
  securities: QuerySecurity[]
) {
  const result = legacySchema.safeParse(parsed);
  if (!result.success) {
    throw new Error(
      "Ukjent eldre JSON-format. Forventer standardParams og documents med id, type og data."
    );
  }
  const legacy: {
    standardParams: Record<string, unknown>;
    documents: { id: string; type: string; data: Record<string, unknown> }[];
  } = result.data;
  const sourceContactId = text(legacy.standardParams.conDbId);
  if (!sourceContactId || sourceContactId !== contactId) {
    throw new Error(
      "Kunden i den eldre eksporten kan ikke bekreftes som valgt kunde."
    );
  }
  if (
    new Set(legacy.documents.map((doc) => doc.id)).size !==
    legacy.documents.length
  ) {
    throw new Error("JSON-filen inneholder dokumenter med samme ID.");
  }
  const warnings = [
    "Eldre eksport: originalvariablene beholdes ved produksjon av urørte ordre. Skjemavisningen er bare delvis gjenopprettet.",
    "Du trenger ikke lagre ordrene på nytt eller fjerne Suitability. Kontroller dokumentforhåndsvisningen før signering.",
    "Hvis du redigerer og lagrer en importert ordre, erstattes originalvariablene med data fra skjemaet. Manglende felt må da fylles ut.",
  ];
  const documents: DocProducerDocument[] = legacy.documents.map((doc) => {
    const values = doc.data;
    if (doc.type === "uploaded") {
      throw new Error(
        "Den eldre eksporten inneholder et vedlegg uten sikre gjenopprettingsdata. Fjern vedlegget fra JSON og last det opp på nytt."
      );
    }
    if (doc.type !== "order") {
      return {
        id: doc.id,
        docType: doc.type,
        processName: doc.type,
        desc: doc.type,
        data: { ...values, legacyExportData: values },
      };
    }
    const portfolio = portfolios.find(
      (port) =>
        (text(values.dpPortfolioId) &&
          String(port.id) === text(values.dpPortfolioId)) ||
        (text(values.portShortName) &&
          port.shortName === text(values.portShortName))
    );
    const isNew = values.isExisting === false || values.dpPortfolioId === "NEW";
    if (!portfolio && !isNew) {
      warnings.push(
        `Ordre ${doc.id}: porteføljen må velges på nytt hvis ordren redigeres; produksjon bruker originalvariablene.`
      );
    }
    const buyOrders: BuyOrderRow[] = [];
    const sellOrders: SellOrderRow[] = [];
    const lines = object(values.manualOrderLines);
    for (const key of Object.keys(lines).filter((key) =>
      /^type\d+$/.test(key)
    )) {
      const index = key.slice(4);
      if (lines[`mt${index}`] === "Rebalansering") continue;
      const code = text(lines[`secIsin${index}`]);
      const security = securities.find(
        (sec) => code && (sec.isin_code === code || sec.security_code === code)
      );
      const name =
        text(lines[`secName${index}`]) ||
        text(lines[`sec${index}`]).split("\n")[0];
      if (!security)
        warnings.push(
          `Ordre ${doc.id}, linje ${index}: instrumentet må velges på nytt hvis ordren redigeres; produksjon bruker originalvariablene.`
        );
      const type = lines[`mt${index}`] === "Limit" ? "Limit" : "Market";
      if (lines[key] === "Kjøp") {
        buyOrders.push({
          id: `${doc.id}-${index}`,
          securityId: security?.id ?? null,
          instrumentCode: security?.security_code ?? code,
          instrumentName: name,
          class1code: security?.class1code ?? "",
          class2id: text(security?.class2id),
          class2code: security?.class2code ?? "",
          isinCode: security?.isin_code ?? code,
          type,
          limitPrice: text(lines[`limit${index}`]),
          holding: "",
          feePercent: "",
          price: text(lines[`marketUnitPrice${index}`]),
          fx: "1",
          quantity: text(lines[`amount${index}`]),
          amountInput: text(lines[`totAmount${index}`]),
          duration: text(lines[`omarketDur${index}`]),
        });
      } else if (lines[key] === "Salg" && security) {
        sellOrders.push({
          securityId: security.id,
          securityCode: security.security_code,
          name,
          class2code: security.class2code,
          holding:
            portfolio?.portfolioReport?.portfolioReportItems?.find(
              (item) => item.security.id === security.id
            )?.amount ?? 0,
          marketUnitPrice: number(lines[`marketUnitPrice${index}`]) ?? 0,
          sellQty: text(lines[`amount${index}`]),
          feePercent: "",
          type,
          limitPrice: text(lines[`limit${index}`]),
          duration: text(lines[`omarketDur${index}`]),
        });
      } else if (lines[key] === "Salg") {
        warnings.push(
          `Ordre ${doc.id}, salgslinje ${index}: kunne ikke gjenopprettes uten instrument-ID; originalen er beholdt i legacyExportData.`
        );
      }
    }
    const newData: Record<string, unknown> = {
      ...values,
      dpPortfolioId: isNew ? "NEW" : portfolio ? String(portfolio.id) : "",
      dpPortfolioName: text(values.portName) || portfolio?.name || "",
      dpPortfolioShortName: text(values.portShortName),
      dpPortType: text(values.dpPortType),
      dpModelType: text(values.dpModelType),
      dpModelTypeName: "",
      dpManagementFee: text(
        values.portfolioManagementFee ?? values.dpManagementFee
      ),
      dpFeeType: values.dpFeeType ?? "",
      dpServiceType: values.dpServiceType ?? "",
      dpOnOffPercentage: text(values.dpSubscriptionFeePercent),
      dpSumAvCash: number(values.dpSumAvCash) ?? 0,
      dpSumInsValue: number(values.dpSumInsValue) ?? 0,
      dpAmountToInvest: number(values.dpAmountToInvest) ?? 0,
      dpOnOffAmount: number(values.dpOnOffAmount) ?? 0,
      dpOnOffDate: text(values.dpOnOffDate),
      dpWithdrawal: number(values.dpWithdrawal) ?? 0,
      dpAccount:
        values.isNewAccount === true ? "newAccount" : text(values.dpAccount),
      dpNewAccount: text(values.dpNewAccount),
      dpIpsWithdrawalAge:
        text(values.dpPortType) === "23"
          ? number(values.portIpsage)
          : undefined,
      dpIpsAnnualSavings:
        text(values.dpPortType) === "23"
          ? number(values.portIpsAmount)
          : undefined,
      dpSavingsAmount: number(values.savAmount),
      dpSavingsDayOfMonth: number(values.savDate),
      dpSavingsAvtalegiro: values.savAG === "Ja",
      dpSavingsFunds: [],
      dpWdAmount: number(values.portFixedWdAmount),
      dpWdDayOfMonth: number(values.portFixedWdDate),
      dpWdAccount: text(values.portWdAccount),
      moveAll: values.movePart === "Hele min beholdning",
      ordreModul: values.dpModelType === "NOMODEL" ? "yes" : "no",
      buyOrders,
      sellOrders,
    };
    // Exported transfer amounts are strings, but the form requires optional numbers.
    for (let index = 1; index <= 6; index++) {
      newData[`Instrument${index}`] = text(values[`Instrument${index}`]);
      newData[`InstrumentAmt${index}`] = number(
        values[`InstrumentAmt${index}`]
      );
    }
    for (let index = 1; index <= 5; index++) {
      const funds = object(values.savingsFunds);
      if (text(funds[`fund${index}Name`])) {
        warnings.push(
          `Ordre ${doc.id}: sparefond og fordeling må fylles inn hvis ordren redigeres; produksjon bruker originalvariablene.`
        );
        break;
      }
    }
    return {
      id: doc.id,
      docType: "order",
      processName: "Order",
      desc: text(newData.dpPortfolioName) || "Importert ordre",
      data: {
        existData: {
          isExisting: !isNew,
          dpPortfolioId: newData.dpPortfolioId,
          dpPortfolioName: newData.dpPortfolioName,
          dpPortfolioShortName: newData.dpPortfolioShortName,
          dpModelType: values.existingModel ?? "",
          dpSumAvCash: newData.dpSumAvCash,
          dpSumInsValue: newData.dpSumInsValue,
        },
        newData,
        legacyExportData: values,
      },
    };
  });
  return {
    version: 1,
    contactId,
    documents,
    modData: {},
    overrides: {
      profile: getImportedProfile(parsed),
      sourceDocIds: documents.map((document) => document.id),
      afterSign: null,
      docProdOut: null,
    },
    warnings,
  };
}
